import React, { useState, useMemo } from "react";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { SectionTitle, Panel, Pill, StatCard } from "@/components/croc/ui";
import { fmtUsd } from "@/lib/format";
import api from "@/lib/api";
import {
  Coins, Gift, Network, Trophy, Sparkles, Award, Zap, Wallet,
  ArrowDownLeft, ArrowUpRight, History, TrendingUp,
} from "lucide-react";

const META = {
  roi_daily:        { label: "Daily ROI", Icon: Coins, kind: "in" },
  direct_bonus:     { label: "Direct Sponsor Bonus", Icon: Gift, kind: "in" },
  level_income:     { label: "Override Income", Icon: Network, kind: "in" },
  rank_commission:  { label: "Rank Commission", Icon: Trophy, kind: "in" },
  same_rank_bonus:  { label: "Same-Rank Bonus", Icon: Award, kind: "in" },
  rank_reward:      { label: "Rank Reward", Icon: Trophy, kind: "in" },
  prediction_bonus: { label: "Prediction Bonus", Icon: Sparkles, kind: "in" },
  croc_pool:        { label: "Croc Pool", Icon: Trophy, kind: "in" },
  share_income:     { label: "Share Income", Icon: Trophy, kind: "in" },
  marketing_point:  { label: "Marketing Points", Icon: Zap, kind: "in" },
  principal_return: { label: "Principal Returned", Icon: ArrowDownLeft, kind: "neutral" },
  principal_withdrawal: { label: "Principal Withdrawal (0% fee)", Icon: ArrowUpRight, kind: "out" },
  claim:            { label: "Claim to Balance", Icon: ArrowDownLeft, kind: "neutral" },
  withdrawal:       { label: "Withdrawal", Icon: ArrowUpRight, kind: "out" },
  withdrawal_reversal: { label: "Withdrawal Refunded (payout failed)", Icon: ArrowDownLeft, kind: "in" },
  mkt_withdraw:     { label: "Marketing Withdrawal", Icon: ArrowUpRight, kind: "out" },
};

const metaFor = (t) =>
  META[t] || { label: (t || "reward").replace(/_/g, " "), Icon: Coins, kind: "in" };

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function IncomeHistory() {
  const { address, user } = useWallet();
  const { data } = usePolling(() => api.rewardsAll(address || "0x0"), [address], 20000);
  const [filter, setFilter] = useState("all");

  const all = data?.rewards ?? [];
  const income = user?.income || {};

  const types = useMemo(() => {
    const set = new Set(all.map((r) => r.type));
    return ["all", ...Array.from(set)];
  }, [all]);

  const rows = useMemo(
    () => (filter === "all" ? all : all.filter((r) => r.type === filter)),
    [all, filter]
  );

  // running total of real income (kind === "in") shown newest→oldest
  const incomeRows = all.filter((r) => metaFor(r.type).kind === "in");
  const incomeTotal = incomeRows.reduce((s, r) => s + (r.amount || 0), 0);

  if (!address) {
    return (
      <div>
        <SectionTitle eyebrow="Earnings Ledger" title="Income History"
          subtitle="Every reward you have earned — ROI, bonuses, overrides, pools and more."
          testid="income-history-title" />
        <Panel data-testid="income-history-connect">
          <div className="text-center py-16 text-muted-foreground">
            <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
            Connect your wallet to view your full income history.
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <SectionTitle
        eyebrow="Earnings Ledger"
        title="Income History"
        subtitle="Every reward you have earned — ROI, bonuses, overrides, pools and more, with exact date and time."
        right={<Pill tone="emerald" data-testid="income-history-total"><TrendingUp className="w-3 h-3" /> {fmtUsd(incomeTotal)} total earned</Pill>}
        testid="income-history-title"
      />

      {/* income breakdown summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <StatCard label="Daily ROI" value={fmtUsd(income.roi)} icon={Coins} accent="emerald" testid="ih-roi" />
        <StatCard label="Direct Bonus" value={fmtUsd(income.direct)} icon={Gift} accent="gold" testid="ih-direct" />
        <StatCard label="Override" value={fmtUsd(income.override)} icon={Network} accent="emerald" testid="ih-override" />
        <StatCard label="Rank" value={fmtUsd(income.rank)} icon={Trophy} accent="gold" testid="ih-rank" />
        <StatCard label="Prediction" value={fmtUsd(income.prediction)} icon={Sparkles} accent="emerald" testid="ih-prediction" />
        <StatCard label="Croc Pool" value={fmtUsd(income.croc_pool)} icon={Trophy} accent="gold" testid="ih-pool" />
        <StatCard label="Share" value={fmtUsd(income.share)} icon={Award} accent="emerald" testid="ih-share" />
      </div>

      {/* filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="income-history-filters">
        {types.map((t) => (
          <button
            key={t}
            data-testid={`income-filter-${t}`}
            onClick={() => setFilter(t)}
            className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors border ${
              filter === t
                ? "bg-[#1CFD4C]/15 text-[#1CFD4C] border-[#1CFD4C]/40"
                : "bg-[#090E0B] text-muted-foreground border-white/10 hover:text-white"
            }`}
          >
            {t === "all" ? "All" : metaFor(t).label}
          </button>
        ))}
      </div>

      {/* ledger table */}
      <Panel data-testid="income-history-table">
        {rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm" data-testid="income-history-empty">
            No income records yet — stake, refer and predict daily to start earning.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-white/10">
                  <th className="py-3 px-2">Type</th>
                  <th className="py-3 px-2">Details</th>
                  <th className="py-3 px-2">Date &amp; Time</th>
                  <th className="py-3 px-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const m = metaFor(r.type);
                  const out = m.kind === "out";
                  const neutral = m.kind === "neutral";
                  const amtCls = out ? "text-red-400" : neutral ? "text-muted-foreground" : "emerald-text";
                  const sign = out ? "−" : neutral ? "" : "+";
                  const from = r.meta?.from;
                  const detail = from ? `from ${from.slice(0, 6)}…${from.slice(-4)}`
                    : r.meta?.title ? r.meta.title
                    : r.meta?.level ? `Level ${r.meta.level}`
                    : r.meta?.rank ? r.meta.rank : "—";
                  return (
                    <tr key={r.id || i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`income-row-${i}`}>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-7 h-7 rounded-full grid place-items-center shrink-0 border ${out ? "bg-red-500/10 border-red-500/25" : "bg-[#1CFD4C]/10 border-[#1CFD4C]/25"}`}>
                            <m.Icon className={`w-3.5 h-3.5 ${out ? "text-red-400" : "emerald-text"}`} />
                          </div>
                          <span className="text-white">{m.label}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-muted-foreground font-num text-xs">{detail}</td>
                      <td className="py-3 px-2 text-muted-foreground font-num text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className={`py-3 px-2 text-right font-num font-bold ${amtCls}`} data-testid={`income-amount-${i}`}>
                        {sign}{fmtUsd(r.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
